import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SendIcon from '@mui/icons-material/Send';
import { NotificationSettings as NotificationSettingsType } from '../types';
import { notificationService } from '../services/notification.service';
import { useAuth } from '../context/AuthContext';
import { Timestamp } from 'firebase/firestore';
import { generateReportData } from './ProductionReport';

const NotificationSettings: React.FC = () => {
  const theme = useTheme();
  const { authUser } = useAuth();
  const [settings, setSettings] = useState<NotificationSettingsType>({
    email: '',
    lifetimeWarning: true,
    lifetimeWarningThreshold: 90,
    transactionReminder: true,
    maintenanceAlert: true,
    dailyReport: false,
    weeklyReport: true,
    customerReport: false,
    customerEmails: [],
    customerReportFrequency: 'monthly',
  });

  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'error'>('success');
  const [isTestingEmail, setIsTestingEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isActivatingSchedule, setIsActivatingSchedule] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);

  useEffect(() => {
    loadNotificationSettings();
  }, [authUser]);

  const loadNotificationSettings = async () => {
    if (!authUser) return;
    
    try {
      setIsLoading(true);
      const userSettings = await notificationService.getUserSettings(authUser.uid);
      
      if (userSettings) {
        setSettings(userSettings);
      }
    } catch (error) {
      console.error('Failed to load notification settings:', error);
      openToast('설정을 불러오는데 실패했습니다.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSwitchChange =
    (field: keyof NotificationSettingsType) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setSettings((prev) => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleTextChange =
    (field: keyof NotificationSettingsType) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        field === 'lifetimeWarningThreshold' ? parseInt(event.target.value, 10) || 0 : event.target.value;

      setSettings((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const openToast = (message: string, severity: 'success' | 'info' | 'error' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleSave = async () => {
    if (!authUser) {
      openToast('로그인이 필요합니다.', 'error');
      return;
    }

    try {
      await notificationService.saveUserSettings(authUser.uid, settings);
      openToast('알림 설정이 저장되었습니다.', 'success');
    } catch (error) {
      console.error('Failed to save notification settings:', error);
      openToast('설정 저장에 실패했습니다.', 'error');
    }
  };

  const handleAddCustomerEmail = () => {
    if (!newCustomerEmail) {
      openToast('이메일을 입력해 주세요.', 'info');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newCustomerEmail)) {
      openToast('유효한 이메일 주소를 입력해 주세요.', 'error');
      return;
    }

    if (settings.customerEmails.includes(newCustomerEmail)) {
      openToast('이미 추가된 이메일입니다.', 'info');
      return;
    }

    setSettings((prev) => ({
      ...prev,
      customerEmails: [...prev.customerEmails, newCustomerEmail],
    }));
    setNewCustomerEmail('');
    openToast('이메일이 추가되었습니다.', 'success');
  };

  const handleRemoveCustomerEmail = (email: string) => {
    setSettings((prev) => ({
      ...prev,
      customerEmails: prev.customerEmails.filter((e) => e !== email),
    }));
  };

  const handleTestEmail = async () => {
    if (!settings.email) {
      openToast('이메일 주소를 입력해 주세요.', 'info');
      return;
    }

    setIsTestingEmail(true);

    try {
      const success = await notificationService.sendTestEmail(settings.email);
      if (success) {
        openToast('테스트 이메일을 전송했습니다. 받은 편지함을 확인해 주세요.', 'success');
      } else {
        openToast('테스트 이메일 전송에 실패했습니다. EmailJS 구성을 확인해 주세요.', 'error');
      }
    } catch (error) {
      console.error('Test email error:', error);
      openToast('테스트 이메일 전송 중 오류가 발생했습니다.', 'error');
    } finally {
      setIsTestingEmail(false);
    }
  };

  // 리포트 예약 활성화/비활성화
  const handleToggleSchedule = async () => {
    if (!authUser) {
      openToast('로그인이 필요합니다.', 'error');
      return;
    }

    if (!settings.customerEmails.length) {
      openToast('고객사 이메일을 먼저 추가해 주세요.', 'error');
      return;
    }

    setIsActivatingSchedule(true);

    try {
      if (settings.reportScheduleActive) {
        // 비활성화
        await notificationService.deactivateReportSchedule(authUser.uid);
        openToast('리포트 예약이 비활성화되었습니다.', 'success');
      } else {
        // 활성화
        await notificationService.activateReportSchedule(authUser.uid);
        openToast('리포트 예약이 활성화되었습니다.', 'success');
      }
      
      // 설정 다시 로드
      await loadNotificationSettings();
    } catch (error) {
      console.error('Schedule toggle error:', error);
      openToast('예약 설정 중 오류가 발생했습니다.', 'error');
    } finally {
      setIsActivatingSchedule(false);
    }
  };

  // 즉시 리포트 발송 (테스트)
  const handleSendReportNow = async () => {
    if (!authUser) {
      openToast('로그인이 필요합니다.', 'error');
      return;
    }

    if (!settings.customerEmails.length) {
      openToast('고객사 이메일을 먼저 추가해 주세요.', 'error');
      return;
    }

    setIsSendingReport(true);

    try {
      // 실제 리포트 데이터 생성
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      openToast('리포트 데이터를 생성하는 중입니다...', 'info');
      
      const reportData = await generateReportData(thirtyDaysAgo, now);

      openToast('이메일을 발송하는 중입니다...', 'info');

      const success = await notificationService.sendProductionReport(
        settings.customerEmails,
        reportData
      );

      if (success) {
        openToast('리포트가 발송되었습니다.', 'success');
      } else {
        openToast('리포트 발송에 실패했습니다.', 'error');
      }
    } catch (error) {
      console.error('Send report error:', error);
      openToast('리포트 발송 중 오류가 발생했습니다.', 'error');
    } finally {
      setIsSendingReport(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 3, md: 4 },
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 1.5, md: 2 }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            알림 설정
          </Typography>
          <Typography variant="body2" sx={{ color: alpha(theme.palette.text.secondary, 0.78) }}>
            금형 상태와 보고서를 이메일로 받아보고, 주요 이벤트를 놓치지 마세요.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{ px: 3.5, py: 1.1, fontWeight: 600, letterSpacing: '0.06em' }}
        >
          설정 저장
        </Button>
      </Stack>

      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
          backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
            theme.palette.primary.dark,
            0.22,
          )})`,
          boxShadow: `0 18px 32px ${alpha(theme.palette.primary.dark, 0.26)}`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            기본 정보
          </Typography>
          <TextField
            fullWidth
            label="알림 수신 이메일"
            type="email"
            value={settings.email}
            onChange={handleTextChange('email')}
            sx={{ mb: 2 }}
            helperText="알림과 보고서를 수신할 이메일 주소"
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleTestEmail}
              disabled={!settings.email || isTestingEmail}
              startIcon={isTestingEmail ? <CircularProgress size={16} /> : undefined}
              sx={{
                borderRadius: 2,
                borderColor: alpha(theme.palette.primary.light, 0.38),
                '&:hover': {
                  borderColor: alpha(theme.palette.primary.light, 0.6),
                  backgroundColor: alpha(theme.palette.primary.light, 0.12),
                },
              }}
            >
              {isTestingEmail ? '전송 중...' : '테스트 이메일 보내기'}
            </Button>
            {!settings.email && (
              <Alert severity="info" sx={{ borderRadius: 2, flexGrow: 1 }}>
                테스트 전 이메일 주소를 입력해 주세요.
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
          backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
            theme.palette.primary.dark,
            0.22,
          )})`,
          boxShadow: `0 18px 32px ${alpha(theme.palette.primary.dark, 0.26)}`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            수명 기반 알림
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: theme.palette.text.secondary }}>
            금형 수명이 임계값에 도달하면 자동으로 이메일 알림을 발송합니다.
          </Typography>
          
          <FormControlLabel
            control={
              <Switch
                checked={settings.lifetimeWarning}
                onChange={handleSwitchChange('lifetimeWarning')}
              />
            }
            label="수명 임박 알림 활성화"
            sx={{ display: 'block', mb: 2 }}
          />
          
          {settings.lifetimeWarning && (
            <Box sx={{ pl: 2, mb: 2, borderLeft: `3px solid ${theme.palette.primary.main}` }}>
              <Typography variant="body2" sx={{ mb: 1.5, color: theme.palette.text.secondary }}>
                알림 임계값 설정
              </Typography>
              <TextField
                type="number"
                label="임계값 (%)"
                value={settings.lifetimeWarningThreshold}
                onChange={handleTextChange('lifetimeWarningThreshold')}
                InputProps={{ inputProps: { min: 50, max: 100, step: 5 } }}
                size="small"
                sx={{ width: 200, mb: 1 }}
              />
              <Alert severity="info" sx={{ borderRadius: 2, mt: 1 }}>
                <Typography variant="caption" display="block">
                  • {settings.lifetimeWarningThreshold}% 도달 시 즉시 알림
                </Typography>
                <Typography variant="caption" display="block">
                  • 시스템이 자동으로 금형 수명을 체크합니다
                </Typography>
              </Alert>
            </Box>
          )}
          
          <FormControlLabel
            control={
              <Switch
                checked={settings.maintenanceAlert}
                onChange={handleSwitchChange('maintenanceAlert')}
              />
            }
            label="정비 필요 알림 (100% 도달 시)"
            sx={{ display: 'block' }}
          />
        </CardContent>
      </Card>

      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
          backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
            theme.palette.primary.dark,
            0.22,
          )})`,
          boxShadow: `0 18px 32px ${alpha(theme.palette.primary.dark, 0.26)}`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            이동 / 거래 알림
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settings.transactionReminder}
                onChange={handleSwitchChange('transactionReminder')}
              />
            }
            label="거래 일정 리마인드"
            sx={{ display: 'block' }}
          />
        </CardContent>
      </Card>

      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
          backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
            theme.palette.primary.dark,
            0.22,
          )})`,
          boxShadow: `0 18px 32px ${alpha(theme.palette.primary.dark, 0.26)}`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            리포트 구독
          </Typography>
          <FormControlLabel
            control={
              <Switch checked={settings.dailyReport} onChange={handleSwitchChange('dailyReport')} />
            }
            label="일간 리포트"
            sx={{ display: 'block', mb: 1 }}
          />
          <FormControlLabel
            control={
              <Switch checked={settings.weeklyReport} onChange={handleSwitchChange('weeklyReport')} />
            }
            label="주간 리포트"
            sx={{ display: 'block' }}
          />
        </CardContent>
      </Card>

      <Card
        sx={{
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.light, 0.22)}`,
          backgroundImage: `linear-gradient(160deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(
            theme.palette.primary.dark,
            0.22,
          )})`,
          boxShadow: `0 18px 32px ${alpha(theme.palette.primary.dark, 0.26)}`,
          backdropFilter: 'blur(12px)',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            고객사 리포트 발송
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={settings.customerReport}
                onChange={handleSwitchChange('customerReport')}
              />
            }
            label="고객사 리포트 활성화"
            sx={{ display: 'block', mb: 2 }}
          />

          {settings.customerReport && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                발송 주기
              </Typography>
              <Select
                fullWidth
                value={settings.customerReportFrequency}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    customerReportFrequency: e.target.value as any,
                  }))
                }
                sx={{ mb: 2 }}
              >
                <MenuItem value="daily">일간</MenuItem>
                <MenuItem value="weekly">주간</MenuItem>
                <MenuItem value="monthly">월간</MenuItem>
                <MenuItem value="quarterly">분기별</MenuItem>
                <MenuItem value="yearly">연간</MenuItem>
              </Select>

              <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                고객사 이메일 목록
              </Typography>
              
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="customer@company.com"
                  value={newCustomerEmail}
                  onChange={(e) => setNewCustomerEmail(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddCustomerEmail();
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleAddCustomerEmail}
                  startIcon={<AddIcon />}
                >
                  추가
                </Button>
              </Stack>

              {settings.customerEmails.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                  {settings.customerEmails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      onDelete={() => handleRemoveCustomerEmail(email)}
                      deleteIcon={<DeleteIcon />}
                      sx={{ borderRadius: 2 }}
                    />
                  ))}
                </Stack>
              )}

              {settings.customerEmails.length === 0 && (
                <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
                  고객사 이메일을 추가해 주세요.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {/* 예약 상태 표시 */}
              {settings.reportScheduleActive && settings.nextReportDate && (
                <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    📅 예약 활성화됨
                  </Typography>
                  <Typography variant="caption" display="block">
                    다음 발송: {settings.nextReportDate.toDate().toLocaleString('ko-KR')}
                  </Typography>
                  {settings.lastReportSent && (
                    <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
                      마지막 발송: {settings.lastReportSent.toDate().toLocaleString('ko-KR')}
                    </Typography>
                  )}
                </Alert>
              )}

              {/* 예약 버튼 */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant={settings.reportScheduleActive ? 'outlined' : 'contained'}
                  color={settings.reportScheduleActive ? 'error' : 'primary'}
                  onClick={handleToggleSchedule}
                  disabled={isActivatingSchedule || !settings.customerEmails.length}
                  startIcon={isActivatingSchedule ? <CircularProgress size={16} /> : <ScheduleIcon />}
                  sx={{ flex: 1 }}
                >
                  {isActivatingSchedule 
                    ? '처리중...' 
                    : settings.reportScheduleActive 
                      ? '예약 취소' 
                      : '예약 활성화'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleSendReportNow}
                  disabled={isSendingReport || !settings.customerEmails.length}
                  startIcon={isSendingReport ? <CircularProgress size={16} /> : <SendIcon />}
                  sx={{ flex: 1 }}
                >
                  {isSendingReport ? '발송중...' : '지금 발송'}
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        ContentProps={{
          sx: {
            borderRadius: 2,
            color: '#fff',
            backgroundColor:
              snackbarSeverity === 'success'
                ? alpha(theme.palette.success.dark, 0.95)
                : snackbarSeverity === 'info'
                ? alpha(theme.palette.primary.dark, 0.9)
                : alpha(theme.palette.error.dark, 0.9),
          },
        }}
      />
    </Box>
  );
};

export default NotificationSettings;
